const MAX_SOURCE_BYTES = 12 * 1024 * 1024
const MAX_UPLOAD_BYTES = 120 * 1024
const AVATAR_SIZE = 256

export type AvatarCrop = { centerX: number; centerY: number; zoom: number }

function canvasBlob(canvas: HTMLCanvasElement, type: string, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => canvas.toBlob(blob => blob ? resolve(blob) : reject(new Error('头像压缩失败，请重试')), type, quality))
}

async function loadImage(file: File): Promise<{ image: CanvasImageSource; width: number; height: number; close: () => void }> {
  if ('createImageBitmap' in window) {
    const bitmap = await createImageBitmap(file)
    return { image: bitmap, width: bitmap.width, height: bitmap.height, close: () => bitmap.close() }
  }
  const url = URL.createObjectURL(file)
  const image = new Image()
  image.decoding = 'async'
  image.src = url
  await image.decode()
  return { image, width: image.naturalWidth, height: image.naturalHeight, close: () => URL.revokeObjectURL(url) }
}

async function toDataUrl(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer())
  let binary = ''
  for (let offset = 0; offset < bytes.length; offset += 0x8000) binary += String.fromCharCode(...bytes.subarray(offset, offset + 0x8000))
  return `data:${blob.type};base64,${btoa(binary)}`
}

export async function compressAvatar(file: File, selection?: AvatarCrop): Promise<string> {
  if (!/^image\/(?:jpeg|png|webp)$/.test(file.type)) throw new Error('请选择 JPG、PNG 或 WebP 图片')
  if (!file.size || file.size > MAX_SOURCE_BYTES) throw new Error('原图不能超过 12 MB')
  const source = await loadImage(file)
  try {
    if (!source.width || !source.height) throw new Error('头像图片无法读取，请重新选择')
    const canvas = document.createElement('canvas')
    canvas.width = AVATAR_SIZE
    canvas.height = AVATAR_SIZE
    const context = canvas.getContext('2d', { alpha: false })
    if (!context) throw new Error('当前浏览器无法处理头像图片')
    const zoom = Math.min(4, Math.max(1, selection?.zoom || 1))
    const crop = Math.min(source.width, source.height) / zoom
    const centerX = Math.min(1, Math.max(0, selection?.centerX ?? .5)) * source.width
    const centerY = Math.min(1, Math.max(0, selection?.centerY ?? .5)) * source.height
    const sx = Math.min(source.width - crop, Math.max(0, centerX - crop / 2))
    const sy = Math.min(source.height - crop, Math.max(0, centerY - crop / 2))
    context.fillStyle = '#eef5fb'
    context.fillRect(0, 0, AVATAR_SIZE, AVATAR_SIZE)
    context.drawImage(source.image, sx, sy, crop, crop, 0, 0, AVATAR_SIZE, AVATAR_SIZE)
    let quality = .76
    let blob = await canvasBlob(canvas, 'image/webp', quality)
    if (blob.type !== 'image/webp') blob = await canvasBlob(canvas, 'image/jpeg', .76)
    while (blob.size > MAX_UPLOAD_BYTES && quality > .46) {
      quality -= .1
      blob = await canvasBlob(canvas, blob.type, quality)
    }
    if (blob.size > MAX_UPLOAD_BYTES) throw new Error('头像压缩后仍然过大，请换一张图片')
    return toDataUrl(blob)
  } finally {
    source.close()
  }
}
